import React, { ReactElement, useContext } from 'react';
import { Navigate, generatePath, useLocation, useParams } from 'react-router-dom';
import { UserContext } from '@/contexts/UserContext';
import { WORKBENCH_URL } from '@/routePaths';

interface Props {
  children: ReactElement;
}

/**
 * Route-level switch for project surfaces. Speakers (role "user") get the
 * unmodified Workbench page — same Transcript / Playback / Segments /
 * Waveform layout as editors. Data scoping happens transparently inside
 * `useGetProject` and `useGetProjectVideoTranscript`, which call the
 * speaker-only eos-app endpoints when the role is "user". Speakers landing
 * on the legacy /projects/:projectId surface are redirected to Workbench
 * since that's the surface they're meant to operate from.
 */
const RoleSwitchedProject = ({ children }: Props): ReactElement => {
  const { userState, isLoadingOrgContext } = useContext(UserContext);
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();

  // Avoid flashing the editor page during the brief window where Auth0 has
  // resolved but the org context (which carries `role`) hasn't loaded yet.
  if (isLoadingOrgContext && !userState?.role) {
    return <></>;
  }

  if (userState?.role === 'user' && projectId) {
    const workbenchPath = generatePath(WORKBENCH_URL, { projectId });
    if (location.pathname !== workbenchPath) {
      return <Navigate to={workbenchPath} replace />;
    }
  }

  return children;
};

export default RoleSwitchedProject;
